import { globalStyles } from "../../../../utils/global-styles";
import { useTV } from "../../../../hooks/useTV";
import { IoCloseSharp } from "react-icons/io5";
import useGlobalStore from "../../../../store/global.store";
import { useEffect, useState, useRef } from "react";
import axios from "../../../../config/axios";
import { useRemote } from "../../../../hooks/useRemote";
import { useConfig } from "../../../../hooks/useConfig";
import VipUpgradePromo from "./VipUpgradePromo";

const VIPUpgrade = () => {
  const { isTv } = useTV();
  const { updateView } = useRemote();

  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  const device = useGlobalStore((state) => state.device);
  const [loading, setLoading] = useState(false);
  const [vipUpgrade, setVipUpgrade] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [agreementError, setAgreementError] = useState("");
  const [showUpgradeForm, setShowUpgradeForm] = useState(false);
  const [phoneLastDigits, setPhoneLastDigits] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [priceConfirmed, setPriceConfirmed] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const statusCheckerInterval = useRef(null);
  const { config } = useConfig();

  const checkUpgradeStatus = () => {
    if (!device._id) return;

    setCheckingStatus(true);
    axios
      .get("/integrations/vip-upgrade", {
        params: {
          device_id: device._id,
        },
      })
      .then((res) => {
        setVipUpgrade(res.data);
        console.log("Status check:", res.data);

        // Clear interval if status is error or success
        if (
          res.data &&
          (res.data.status === "error" || res.data.status === "success")
        ) {
          clearInterval(statusCheckerInterval.current);
          statusCheckerInterval.current = null;
        }

        if (res.data.status === "success") {
          console.log("Updating view to VIP");
          updateView("VIP");
        }
      })
      .catch((err) => {
        console.log(err);
        setPhoneError(err.response?.data?.message || "Failed to check status");
      })
      .finally(() => {
        setCheckingStatus(false);
      });
  };

  const startStatusChecker = () => {
    // Clear any existing interval first
    if (statusCheckerInterval.current) {
      clearInterval(statusCheckerInterval.current);
    }

    // Start a new checker that runs every minute
    statusCheckerInterval.current = setInterval(checkUpgradeStatus, 30000);
  };

  useEffect(() => {
    setShowBottomBar(false);
    setLoading(true);
    axios
      .get("/integrations/vip-upgrade", {
        params: {
          device_id: device._id,
        },
      })
      .then((res) => {
        setVipUpgrade(res.data);
        console.log(res.data);

        // If there's existing data and status is pending, start checker
        if (
          res.data &&
          (res.data.status === "pending" || res.data.status === "preparing")
        ) {
          startStatusChecker();
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });

    // Cleanup interval on component unmount
    return () => {
      if (statusCheckerInterval.current) {
        clearInterval(statusCheckerInterval.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = () => {
    let hasError = false;

    if (phoneLastDigits.length !== 4) {
      setPhoneError("Please enter the last 4 digits of your phone number");
      hasError = true;
    } else {
      setPhoneError("");
    }

    if (!priceConfirmed) {
      setAgreementError("Please confirm the terms for VIP upgrade");
      hasError = true;
    } else {
      setAgreementError("");
    }

    if (hasError) return;

    setSubmitting(true);
    setCheckingStatus(true);
    axios
      .post("/integrations/vip-upgrade", {
        device_id: device._id,
        phone_last_4_digits: phoneLastDigits,
      })
      .then((res) => {
        console.log(res.data);

        // Show that we're checking instead of showing form
        setShowUpgradeForm(false);

        // Immediately check status instead of waiting for interval
        checkUpgradeStatus();

        // Start the interval checker for subsequent checks
        startStatusChecker();
      })
      .catch((err) => {
        console.log(err);
        // Display error within the form instead of creating a separate error state
        setPhoneError(
          err.response?.data?.message ||
            "Invalid phone number or something went wrong"
        );
        setSubmitting(false);
        setCheckingStatus(false);
      });
  };

  const handleClose = () => {
    updateView("Home", () => {
      setShowBottomBar(true);
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "#2ecc71";
      case "failed":
        return "#e74c3c";
      case "pending":
        return "#f39c12";
      case "preparing":
        return "#2ecc71";
      default:
        return "#333";
    }
  };
  const getStatusLabel = (status) => {
    switch (status) {
      case "pending":
        return "UPGRADING";
      case "preparing":
        return "SUCCESS!";
      case "success":
        return "Complete!";
      case "failed":
        return "SOMETHING WENT WRONG";
      default:
        return "Checking...";
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: isInputFocused && !isTv ? "flex-start" : "center",
        position: "fixed",
        top: 0,
        left: 0,
        backgroundImage: `url("/static/vip/night.jpg")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: "white",
        width: "100vw",
        height: "100vh",
        zIndex: 50,
        overflowY: "auto",
        paddingTop: isInputFocused && !isTv ? "20px" : "0",
        opacity: "100%",
      }}
    >
      {!isTv && (vipUpgrade || showUpgradeForm || checkingStatus) && (
        <button
          tabIndex={0}
          onClick={handleClose}
          style={{
            position: "absolute",
            top: "5%",
            right: "3%",
            background: "transparent",
            padding: "5px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
          }}
        >
          <IoCloseSharp size={isTv ? 100 : 60} color="white" />
        </button>
      )}

      {loading && (
        <span
          style={{
            marginLeft: "10px",
            border: "none",
            background: "transparent",
            color: "blue",
            fontFamily: "Lato",
            fontSize: "40px",
            padding: "0px 20px",
            borderRadius: "20px",
            fontWeight: "700",
            margin: "10px 0px",
            cursor: "pointer",
          }}
        >
          Loading...
        </span>
      )}

      {checkingStatus && !vipUpgrade && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f8f8f8",
            padding: isTv ? "40px" : "30px",
            borderRadius: "15px",
            boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
            minWidth: isTv ? "600px" : "350px",
            textAlign: "center",
          }}
        >
          <h1
            style={{
              fontFamily: "Lato",
              fontSize: isTv ? "50px" : "32px",
              color: "#333",
              marginBottom: "20px",
            }}
          >
            Processing Your Request
          </h1>

          <div
            style={{
              fontSize: isTv ? "24px" : "18px",
              fontFamily: "Lato",
              color: "#666",
              marginBottom: "20px",
            }}
          >
            Please wait while we process your VIP upgrade...
          </div>

          <div
            style={{
              width: isTv ? "60px" : "40px",
              height: isTv ? "60px" : "40px",
              border: "5px solid #f3f3f3",
              borderTop: "5px solid #3498db",
              borderRadius: "50%",
              animation: "spin 2s linear infinite",
              marginBottom: "20px",
            }}
          ></div>

          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      )}

      {vipUpgrade && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f8f8f8",
            padding: isTv ? "40px" : "30px",
            borderRadius: "15px",
            boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
            minWidth: isTv ? "600px" : "350px",
            textAlign: "center",
          }}
        >
          <h1
            style={{
              fontFamily: "Lato",
              fontSize: isTv ? "50px" : "32px",
              color: "#333",
              marginBottom: "20px",
            }}
          >
            VIP Upgrade Status
          </h1>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginBottom: "25px",
            }}
          >
            <div
              style={{
                fontSize: isTv ? "32px" : "30px",
                fontFamily: "Lato",
                fontWeight: "bold",
                color: getStatusColor(vipUpgrade.status),
                textTransform: "uppercase",
                marginBottom: "10px",
              }}
            >
              {getStatusLabel(vipUpgrade.status)}
            </div>

            {vipUpgrade.status === "pending" && (
              <div
                style={{
                  marginTop: "10px",
                  fontSize: isTv ? "24px" : "24px",
                  fontFamily: "Lato",
                  color: "#666",
                }}
              >
                Your upgrade is being processed. This page will update
                automatically within the next 5-10 minutes.
              </div>
            )}

            {vipUpgrade.status === "preparing" && (
              <div
                style={{
                  marginTop: "10px",
                  fontSize: isTv ? "24px" : "24px",
                  fontFamily: "Lato",
                  color: "#666",
                  textAlign: "center",
                  fontWeight: "500",
                }}
              >
                VIP package has been added! Please allow 5-10 minutes to gain
                access to the VIP lounge.
              </div>
            )}

            {vipUpgrade.status === "failed" && vipUpgrade.error_message && (
              <div
                style={{
                  marginTop: "10px",
                  fontSize: isTv ? "24px" : "24px",
                  fontFamily: "Lato",
                  color: "#e74c3c",
                }}
              >
                Error: {vipUpgrade.error_message}
              </div>
            )}
          </div>

          {vipUpgrade.status === "failed" ? (
            <div style={{ display: "flex", gap: "15px" }}>
              <button
                style={{
                  ...globalStyles.button(isTv),
                  backgroundColor: "blue",
                  fontSize: isTv ? "40px" : "35px",
                  color: "white",
                  borderColor: "#3498db",
                }}
                onClick={() => {
                  setVipUpgrade(null);
                  setCheckingStatus(false);
                  setSubmitting(false);
                  setPhoneError("");
                  setAgreementError("");
                  setShowUpgradeForm(true);
                }}
              >
                TRY AGAIN
              </button>
              <button style={globalStyles.button(isTv)} onClick={handleClose}>
                CLOSE
              </button>
            </div>
          ) : (
            <button style={globalStyles.button(isTv)} onClick={handleClose}>
              {vipUpgrade.status === "success" ? "CONTINUE" : "CLOSE"}
            </button>
          )}
        </div>
      )}

      {!loading && !vipUpgrade && !showUpgradeForm && !checkingStatus && (
        <VipUpgradePromo
          onUpgrade={() => {
            setShowUpgradeForm(true);
            setPhoneError("");
            setAgreementError("");
          }}
          onCancel={handleClose}
        />
        // <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        //   <button
        //     style={globalStyles.button(isTv)}
        //     onClick={() => {
        //       setShowUpgradeForm(true);
        //       setPhoneError("");
        //       setAgreementError("");
        //     }}
        //   >
        //     UPGRADE NOW!
        //   </button>
        //   <button
        //     style={{
        //       ...globalStyles.button(isTv),
        //       color: "red",
        //       borderColor: "red",
        //     }}
        //     onClick={handleClose}
        //   >
        //     NO THANKS
        //   </button>
        // </div>
      )}

      {showUpgradeForm && !submitting && (
        <div
          style={{
            backgroundImage: `url("/static/vip/night.jpg")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            width: "100vw",
            height: "100vh",
            padding: "40px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            boxSizing: "border-box",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "24px",
              padding: isTv ? "60px" : "30px",
              width: isTv ? "900px" : "90vw",
              maxWidth: "1000px",
              boxShadow: "0px 8px 40px rgba(0, 0, 0, 0.4)",
              overflowY: "auto",
              maxHeight: "95vh",
              position: "relative",
            }}
          >
            <h1
              style={{
                fontFamily: "Lato",
                fontSize: isTv ? "50px" : "32px",
                fontWeight: "bold",
                color: "#333",
                marginBottom: "20px",
                textAlign: "center",
              }}
            >
              {device?.subscription?.subscription_name
                ?.toLowerCase()
                ?.includes("month")
                ? config?.VipUpgradeCopy?.HeadlineMonthly ||
                  "Early Access VIP Upgrade — Only +$4.99/mo"
                : config?.VipUpgradeCopy?.HeadlineYearly ||
                  "Early Access VIP Upgrade — Only +$60/yr"}
            </h1>
            <div
              style={{
                width: "100%",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              <span
                style={{
                  fontSize: isTv ? "26px" : "25px",
                  fontFamily: "Lato",
                  fontWeight: 400,
                  color: "#2c3e50",
                  marginBottom: "20px",
                  textAlign: "center",
                }}
              >
                {device.subscription.subscription_name
                  .toLowerCase()
                  .includes("month")
                  ? config?.VipUpgradeCopy?.Monthly ||
                    "Enjoy full access to the VIP Package for only +$4.99/mo. Risk Free, Cancel Anytime."
                  : config?.VipUpgradeCopy?.Yearly ||
                    "Enjoy full access to the VIP Package for only +$60/yr. Risk Free, Cancel Anytime."}
              </span>
            </div>

            {/* ALWAYS SHOW CHECKBOX FIRST */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                width: "100%",
                margin: "15px 0 5px 0",
                cursor: "pointer",
              }}
              onClick={() => {
                setPriceConfirmed(!priceConfirmed);
                if (agreementError) setAgreementError("");
              }}
            >
              <div
                style={{
                  width: isTv ? "36px" : "34px",
                  height: isTv ? "36px" : "34px",
                  border: agreementError
                    ? "2px solid #e74c3c"
                    : "2px solid #3498db",
                  borderRadius: "4px",
                  marginRight: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: priceConfirmed ? "blue" : "transparent",
                }}
              >
                {priceConfirmed && (
                  <div
                    style={{
                      color: "white",
                      fontSize: isTv ? "28px" : "18px",
                      fontWeight: "bold",
                    }}
                  >
                    ✓
                  </div>
                )}
              </div>
              <span
                style={{
                  fontSize: isTv ? "22px" : "18px",
                  color: "#555",
                  fontFamily: "Lato",
                }}
              >
                {device.subscription.subscription_name
                  .toLowerCase()
                  .includes("month")
                  ? config?.VipUpgradeCopy?.TermsMonthly ||  
                    "I understand that the VIP Package will be added to my existing subscription for +$4.99/month and that I can cancel anytime."
                  : config?.VipUpgradeCopy?.TermsYearly ||  
                  "I understand that the VIP Package will be added to my existing subscription for +$55/year and that I can cancel anytime."}
              </span>
            </div>

            {/* ERROR BELOW CHECKBOX IF NEEDED */}
            {agreementError && (
              <div
                style={{
                  color: "#e74c3c",
                  fontSize: isTv ? "22px" : "15px",
                  fontFamily: "Lato",
                  marginBottom: "10px",
                  fontWeight: "500",
                  width: "100%",
                  textAlign: "left",
                  paddingLeft: isTv ? "48px" : "36px",
                }}
              >
                {agreementError}
              </div>
            )}

            {/* ONLY SHOW PHONE INPUT IF CHECKBOX IS CONFIRMED */}
            {priceConfirmed && (
              <>
                <div
                  style={{
                    width: "100%",
                    textAlign: "center",
                    marginTop: "0px",
                    marginBottom: "3px",
                  }}
                >
                  <span
                    style={{
                      fontSize: isTv ? "22px" : "22px",
                      fontFamily: "Lato",
                      color: "#007bff",
                      fontWeight: "bold",
                    }}
                  >
                    For your security, enter the last 4 digits of the phone
                    number tied to your subscription
                  </span>
                </div>

                <input
                  type="text"
                  placeholder="Last 4 Digits of Phone Number"
                  value={phoneLastDigits}
                  onChange={(e) => {
                    setPhoneLastDigits(e.target.value.slice(0, 4));
                    if (phoneError) setPhoneError("");
                  }}
                  onFocus={() => {
                    setIsInputFocused(true);
                    setShowBottomBar(false);
                  }}
                  onBlur={() => {
                    setIsInputFocused(false);
                    // setShowBottomBar(true);
                  }}
                  maxLength={4}
                  style={{
                    width: "100%",
                    padding: isTv ? "20px" : "12px",
                    margin: "15px 0 5px 0",
                    borderRadius: "10px",
                    border: phoneError ? "2px solid #e74c3c" : "2px solid #ddd",
                    fontSize: isTv ? "30px" : "30px",
                    fontFamily: "Lato",
                    textAlign: "center",
                    outline: "none",
                  }}
                />

                {phoneError && (
                  <div
                    style={{
                      color: "#e74c3c",
                      fontSize: isTv ? "22px" : "15px",
                      fontFamily: "Lato",
                      marginBottom: "10px",
                      fontWeight: "500",
                      width: "100%",
                      textAlign: "center",
                    }}
                  >
                    {phoneError}
                  </div>
                )}
              </>
            )}

            <button
              style={{
                ...globalStyles.button(isTv),
                width: "100%",
                marginTop: "15px",
                opacity: submitting ? 0.7 : 1,
                cursor: submitting ? "not-allowed" : "pointer",
                backgroundColor: priceConfirmed ? "#007bff" : "#f8f8f8",
                color: priceConfirmed ? "white" : "#666",
              }}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "SUBMITTING..." : "UPGRADE NOW!"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VIPUpgrade;
